import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import styles from "./MailboxWhatsApp.module.css";

import AccountActionModal from "../modals/AccountActionModal";
import Button from "../../../ui/buttons/Button";
import { ReactComponent as WhatsAppIcon } from "../../../assets/icons/whatsapp.svg";
import { whatsappService } from "../../../services/whatsapp.service";
import { loadFacebookSdk, openEmbeddedSignup } from "../../../utils/facebookSdk";
import { extractApiErrorMessage } from "../../../utils/apiError";
import { whatsappTemplatesPath } from "../../../utils/whatsappRoutes";

export const CONNECT_LABEL = "Conectar WhatsApp";
export const CONNECT_ANOTHER_LABEL = "Conectar otro número";
export const CONTINUE_IN_META_LABEL = "Continuar en Meta";

const connectDescription = (mailbox) => (
  <>
    <p>
      El número tiene que estar en uso con la app WhatsApp Business en un teléfono:
      sigue funcionando ahí y lo que se escriba desde el celular también aparece en
      MaqMailing.
    </p>
    <p>
      En la app, Meta desactiva los mensajes temporales, &quot;ver una vez&quot; y la
      ubicación en tiempo real, y deja las listas de difusión en solo lectura.
    </p>
    <p>
      Las conversaciones las van a ver solo quienes tienen acceso a{" "}
      <strong>{mailbox?.name || mailbox?.email}</strong>. Vas a iniciar sesión en Meta
      y confirmar el número desde el teléfono.
    </p>
  </>
);

const connectedNotice = (connection) => {
  const number = connection.display_phone_number || connection.phone_number_id;
  if (!connection.on_business_app) {
    return `Conectaste ${number}, pero Meta lo registró solo en la API: no quedó en la app del teléfono.`;
  }
  if (connection.history_sync_error) {
    return `Conectaste ${number}. Sigue en el teléfono, pero la agenda y el historial no se pudieron pedir: ${connection.history_sync_error}`;
  }
  return `Conectaste ${number}. Sigue en el teléfono; la agenda y el historial de chats van a ir apareciendo en los próximos minutos.`;
};

export const LOAD_ERROR_MESSAGE = "No se pudieron consultar los números de WhatsApp. Probá de nuevo en un rato.";

export function useMailboxWhatsApp(enabled = true) {
  const [connections, setConnections] = useState([]);
  const [adminMailAccountIds, setAdminMailAccountIds] = useState([]);
  const [loadError, setLoadError] = useState(null);

  const reload = useCallback(async () => {
    try {
      const { results, adminMailAccountIds: adminIds } = await whatsappService.listAccounts();
      setConnections(results);
      setAdminMailAccountIds(adminIds);
      setLoadError(null);
    } catch (requestError) {
      setLoadError(extractApiErrorMessage(requestError) || LOAD_ERROR_MESSAGE);
    }
  }, []);

  useEffect(() => {
    if (enabled) reload();
  }, [reload, enabled]);

  const connectionsOf = useCallback(
    (mailAccountId) =>
      connections.filter((connection) => Number(connection.mail_account) === Number(mailAccountId)),
    [connections],
  );
  const administers = useCallback(
    (mailAccountId) => adminMailAccountIds.some((adminId) => Number(adminId) === Number(mailAccountId)),
    [adminMailAccountIds],
  );

  return { connectionsOf, administers, reload, loadError, enabled };
}

export default function MailboxWhatsApp({
  mailAccount,
  connections = [],
  canManage = false,
  loadError = null,
  onChanged,
}) {
  const [confirming, setConfirming] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [disconnectingId, setDisconnectingId] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  if (!connections.length && !canManage) return null;

  const handleConnect = async () => {
    setConfirming(false);
    if (connecting) return;
    setConnecting(true);
    setError(null);
    setNotice(null);
    try {
      const start = await whatsappService.startConnection(mailAccount.id);
      const sdk = await loadFacebookSdk();
      const { code, state, phoneNumberId, wabaId } = await openEmbeddedSignup({
        sdk,
        appId: start.app_id,
        configId: start.config_id,
        graphVersion: start.graph_version,
        state: start.state,
        featureType: start.feature_type || "",
      });
      const connection = await whatsappService.completeConnection({ state, code, phoneNumberId, wabaId });
      await onChanged?.();
      setNotice(connectedNotice(connection));
    } catch (requestError) {
      setError(
        extractApiErrorMessage(requestError) ||
          requestError?.message ||
          "No se pudo conectar el número de WhatsApp.",
      );
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async (connectionId) => {
    if (disconnectingId) return;
    setDisconnectingId(connectionId);
    setError(null);
    setNotice(null);
    try {
      await whatsappService.disconnect(connectionId);
      await onChanged?.();
      setNotice("El número quedó desconectado.");
    } catch (requestError) {
      setError(
        extractApiErrorMessage(requestError) || "No se pudo desconectar el número de WhatsApp.",
      );
    } finally {
      setDisconnectingId(null);
    }
  };

  return (
    <div className={styles.section} aria-label={`WhatsApp de ${mailAccount.name || mailAccount.email}`}>
      <div className={styles.label}>WhatsApp</div>

      {connections.length > 0 && (
        <ul className={styles.numbers}>
          {connections.map((connection) => (
            <li className={styles.number} key={connection.id}>
              <span className={styles.phone}>
                {connection.display_phone_number || connection.phone_number_id}
              </span>
              {connection.verified_name && (
                <span className={styles.detail}>{connection.verified_name}</span>
              )}
              {connection.on_business_app && (
                <span className={styles.badge}>También en el teléfono</span>
              )}
              {connection.history_sync_error && (
                <span className={styles.warning}>
                  Historial no sincronizado: {connection.history_sync_error}
                </span>
              )}
              <span className={styles.numberActions}>
                <Link className={styles.action} to={whatsappTemplatesPath(connection.id)}>
                  Plantillas
                </Link>
                {canManage && (
                  <button
                    type="button"
                    className={`${styles.action} ${styles.danger}`}
                    onClick={() => handleDisconnect(connection.id)}
                    disabled={disconnectingId === connection.id}
                    aria-label={`Desconectar ${connection.display_phone_number || connection.phone_number_id}`}
                  >
                    {disconnectingId === connection.id ? "Desconectando..." : "Desconectar"}
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      {loadError && canManage && (
        <p className={styles.error} role="alert">
          {loadError}
        </p>
      )}

      {canManage && (
        <div>
          <Button
            variant="primary"
            tone="green"
            iconLeft={<WhatsAppIcon aria-hidden="true" />}
            loading={connecting}
            disabled={Boolean(loadError)}
            onClick={() => setConfirming(true)}
          >
            {connecting ? "Conectando..." : connections.length ? CONNECT_ANOTHER_LABEL : CONNECT_LABEL}
          </Button>
        </div>
      )}

      {notice && (
        <p className={styles.notice} role="status">
          {notice}
        </p>
      )}
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}

      <AccountActionModal
        isOpen={confirming}
        title={`Conectar WhatsApp a ${mailAccount.name || mailAccount.email}`}
        description={connectDescription(mailAccount)}
        fields={[]}
        submitText={CONTINUE_IN_META_LABEL}
        onCancel={() => setConfirming(false)}
        onSubmit={handleConnect}
      />
    </div>
  );
}
